import { HttpErrorResponse } from '@angular/common/http';

/**
 * Extrai uma mensagem de erro clara, legível e fiel a partir de qualquer
 * estrutura retornada pelo backend ou exceção no frontend.
 */
export function extractErrorMessage(
  err: unknown,
  fallbackMessage = 'Ocorreu um erro inesperado. Tente novamente.',
): string {
  if (!err) {
    return fallbackMessage;
  }

  // Se já for uma string simples
  if (typeof err === 'string') {
    const parsed = cleanAndSanitizeMessage(err);
    return parsed || fallbackMessage;
  }

  // Se for uma instância padrão de Error do JS
  if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
    const msg = cleanAndSanitizeMessage(err.message);
    return msg || fallbackMessage;
  }

  // Se for HttpErrorResponse do Angular
  if (err instanceof HttpErrorResponse) {
    // 1. Verificar estado de conexão / rede
    if (!navigator.onLine || err.status === 0) {
      return 'Sem ligação à internet ou servidor inacessível. Verifique a sua conexão.';
    }

    // 2. Tentar extrair do corpo da resposta (err.error)
    const backendMsg = parseBackendPayload(err.error);
    if (backendMsg) {
      return backendMsg;
    }

    // 3. Fallback inteligente baseado no código HTTP se o corpo não tiver mensagem
    const httpStatusMessage = getStatusFallbackMessage(err.status);
    if (httpStatusMessage) {
      return httpStatusMessage;
    }

    // 4. Se houver statusText com significado
    if (err.statusText && err.statusText !== 'OK' && err.statusText !== 'Unknown Error') {
      return err.statusText;
    }

    return fallbackMessage;
  }

  // Se for um objecto genérico (ex: payload de erro direto)
  if (typeof err === 'object') {
    const msg = parseBackendPayload(err);
    if (msg) return msg;
  }

  return fallbackMessage;
}

/**
 * Extrai a mensagem de sucesso a partir de payloads do backend
 */
export function extractSuccessMessage(
  res: unknown,
  fallbackMessage = 'Operação realizada com sucesso.',
): string {
  if (!res) return fallbackMessage;

  if (typeof res === 'string') {
    const cleaned = cleanAndSanitizeMessage(res);
    return cleaned || fallbackMessage;
  }

  if (typeof res === 'object' && res !== null) {
    const obj = res as Record<string, any>;
    const retorno = obj['retorno'] as Record<string, any> | undefined;

    const directMsg =
      readString(obj['mensagem']) ||
      readString(obj['message']) ||
      readString(retorno?.['mensagem']) ||
      readString(retorno?.['message']) ||
      readString(obj['description']) ||
      readString(obj['descricao']);

    if (directMsg) return directMsg;
  }

  return fallbackMessage;
}

/**
 * Inspeciona recursivamente o payload retornado pelo servidor
 */
function parseBackendPayload(payload: unknown): string {
  if (!payload) return '';

  // Se o payload for uma string
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) return '';

    // Se for JSON codificado em string, tentar fazer parse
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsedJson = JSON.parse(trimmed);
        return parseBackendPayload(parsedJson);
      } catch {
        // Não é JSON válido, segue como string normal
      }
    }

    return cleanAndSanitizeMessage(trimmed);
  }

  // Se o payload for um array
  if (Array.isArray(payload)) {
    const items = payload
      .map((item) => parseBackendPayload(item))
      .filter(Boolean);
    return items.join('\n');
  }

  // Se o payload for um objeto
  if (typeof payload === 'object' && payload !== null) {
    const data = payload as Record<string, any>;
    const retorno = data['retorno'] as Record<string, any> | undefined;
    const dados = (data['dados'] || data['data']) as Record<string, any> | undefined;

    // 1. Dicionário de validação ASP.NET / FluentValidation (errors: { Campo: ["Erro 1", "Erro 2"] })
    if (data['errors'] || data['erros']) {
      const validationMsgs = extractValidationErrors(data['errors'] || data['erros']);
      if (validationMsgs) return validationMsgs;
    }

    // 2. Array ou lista de notificações / errorMessages
    if (Array.isArray(data['errorMessages']) && data['errorMessages'].length) {
      return data['errorMessages'].map(readString).filter(Boolean).join('\n');
    }
    if (Array.isArray(data['notificacoes']) && data['notificacoes'].length) {
      return data['notificacoes'].map(readString).filter(Boolean).join('\n');
    }
    if (Array.isArray(data['notifications']) && data['notifications'].length) {
      return data['notifications'].map(readString).filter(Boolean).join('\n');
    }

    // 3. Campos padrão em português e inglês mais comuns no SecurPro e APIs .NET
    const directCandidate =
      readString(data['mensagem']) ||
      readString(retorno?.['mensagem']) ||
      readString(retorno?.['descricao']) ||
      readString(retorno?.['message']) ||
      readString(data['message']) ||
      readString(data['detail']) ||
      readString(data['descricao']) ||
      readString(data['description']) ||
      readString(data['motivo']) ||
      readString(data['reason']) ||
      readString(data['error']) ||
      readString(dados?.['mensagem']) ||
      readString(dados?.['message']);

    if (directCandidate) {
      return cleanAndSanitizeMessage(directCandidate);
    }

    // 4. Se tiver apenas title (ProblemDetails) e não for um título genérico
    const title = readString(data['title']);
    if (title && !isGenericTitle(title)) {
      return cleanAndSanitizeMessage(title);
    }
  }

  return '';
}

/**
 * Trata erros de validação em formato de dicionário ou lista
 */
function extractValidationErrors(errors: unknown): string {
  if (!errors) return '';

  if (typeof errors === 'string') {
    return cleanAndSanitizeMessage(errors);
  }

  if (Array.isArray(errors)) {
    return errors
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, any>;
          return (
            readString(obj['errorMessage']) ||
            readString(obj['message']) ||
            readString(obj['mensagem']) ||
            readString(obj['error']) ||
            ''
          );
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }

  if (typeof errors === 'object' && errors !== null) {
    const errorDict = errors as Record<string, any>;
    const messages: string[] = [];

    for (const [key, value] of Object.entries(errorDict)) {
      if (Array.isArray(value)) {
        const fieldMsgs = value.map(readString).filter(Boolean);
        if (fieldMsgs.length > 0) {
          messages.push(fieldMsgs.join('; '));
        }
      } else if (typeof value === 'string' && value.trim()) {
        messages.push(value.trim());
      }
    }

    return messages.filter(Boolean).join('\n');
  }

  return '';
}

/**
 * Sanitiza o texto, remove tags HTML caso o servidor devolva uma página de erro
 * e limpa prefixos desnecessários.
 */
function cleanAndSanitizeMessage(msg: string): string {
  if (!msg) return '';

  let sanitized = msg.trim();

  // Detetar se a resposta é uma página HTML de crash do servidor/proxy
  if (
    sanitized.toLowerCase().startsWith('<!doctype html') ||
    sanitized.toLowerCase().startsWith('<html') ||
    sanitized.includes('</html>') ||
    sanitized.includes('</div>')
  ) {
    // É uma página HTML (ex: NGINX / IIS / ASP.NET Yellow Screen)
    return 'Ocorreu uma falha no servidor. Por favor, tente novamente mais tarde.';
  }

  // Remove tags HTML soltas se houver
  sanitized = sanitized.replace(/<[^>]*>?/gm, '');

  // Remove prefixos como "Error: " ou "Validation failed: "
  sanitized = sanitized.replace(/^Error:\s*/i, '');
  sanitized = sanitized.replace(/^Exception:\s*/i, '');

  return sanitized.trim();
}

function readString(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  return '';
}

function isGenericTitle(title: string): boolean {
  const normalized = title.trim().toLowerCase();
  return (
    normalized === 'one or more validation errors occurred.' ||
    normalized === 'bad request' ||
    normalized === 'an error occurred while processing your request.' ||
    normalized === 'internal server error' ||
    normalized === 'error'
  );
}

/**
 * Mensagens amigáveis em português quando o backend não envia payload informativo
 */
function getStatusFallbackMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Dados do pedido inválidos. Verifique as informações fornecidas.';
    case 401:
      return 'Sessão expirada ou não autorizada. Faça login novamente.';
    case 403:
      return 'Não tem permissão para aceder ou realizar esta operação.';
    case 404:
      return 'O registo ou recurso solicitado não foi encontrado.';
    case 405:
      return 'Operação não permitida pelo servidor.';
    case 408:
      return 'O servidor demorou demasiado tempo a responder (Tempo limite excedido).';
    case 409:
      return 'Conflito com dados existentes (o registo já existe ou está a ser utilizado).';
    case 413:
      return 'O ficheiro enviado é demasiado grande para o limite permitido.';
    case 415:
      return 'Formato de ficheiro ou formato de dados não suportado.';
    case 422:
      return 'Não foi possível processar os dados fornecidos. Verifique os campos.';
    case 429:
      return 'Muitas tentativas em pouco tempo. Aguarde alguns instantes.';
    case 500:
      return 'Erro interno do servidor. Se o problema persistir, contacte o suporte.';
    case 502:
      return 'Serviço temporariamente indisponível (Bad Gateway).';
    case 503:
      return 'Serviço em manutenção ou sobrecarregado. Tente dentro de alguns momentos.';
    case 504:
      return 'O servidor não respondeu a tempo (Gateway Timeout). Tente novamente.';
    default:
      return '';
  }
}
